import { useCallback, useEffect, useMemo, useState } from 'react';

import { settingsApi } from '../../api';
import { Badge, Button, ErrorState, Loading, TextArea } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { relativeTime } from '../../utils/format';
import '../pages.css';

/**
 * Integrations: how other systems may send images to POST /predict.
 *
 * Today that is one setting — which hosts the API server may download `image_urls`
 * from. Everyone can read it (an integrator should be able to see why a URL was
 * refused); only an administrator can change it, and the API enforces that.
 */

const MODES = [
  {
    value: 'off',
    label: 'Off',
    hint: 'image_urls is refused. Integrations send files, base64 or image ids.',
  },
  {
    value: 'allowlist',
    label: 'Only these hosts',
    hint: 'Downloads only from the hosts listed below. Recommended.',
  },
  {
    value: 'any',
    label: 'Any public host',
    hint: 'Any address on the public internet. Internal addresses are still refused.',
  },
];

function hostsFromText(text) {
  return text
    .split(/[\n,]+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function sameList(a, b) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export function IntegrationsTab() {
  const toast = useToast();
  const { isAdmin } = useAuth();

  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('off');
  const [hostsText, setHostsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState(null);

  const apply = useCallback((payload) => {
    setRecord(payload);
    setMode(payload.mode);
    setHostsText((payload.allowed_hosts || []).join('\n'));
    setFieldError(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await settingsApi.imageUrls());
      setError(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hosts = useMemo(() => hostsFromText(hostsText), [hostsText]);
  const dirty = record && (mode !== record.mode || !sameList(hosts, record.allowed_hosts || []));
  const needsHosts = mode === 'allowlist' && hosts.length === 0;

  const save = useCallback(async () => {
    setSaving(true);
    setFieldError(null);
    try {
      const saved = await settingsApi.updateImageUrls({ mode, allowed_hosts: hosts });
      apply(saved);
      toast.success(
        'Image URL settings saved',
        saved.mode === 'off'
          ? 'POST /predict no longer downloads image URLs.'
          : saved.mode === 'any'
            ? 'POST /predict may download images from any public host.'
            : `POST /predict may download images from ${saved.allowed_hosts.length} host${
                saved.allowed_hosts.length === 1 ? '' : 's'
              }.`,
      );
    } catch (cause) {
      const rows = cause?.details?.errors;
      if (Array.isArray(rows) && rows.length) {
        setFieldError(rows.map((row) => row.message).join(' · '));
      } else {
        toast.error(cause, { title: 'Could not save the image URL settings' });
      }
    } finally {
      setSaving(false);
    }
  }, [apply, hosts, mode, toast]);

  if (loading && !record) return <Loading label="Reading integration settings…" large />;
  if (error && !record) return <ErrorState error={error} onRetry={refresh} />;

  const limits = record.limits || {};
  const modeHint = MODES.find((option) => option.value === mode)?.hint;

  return (
    <div className="col" style={{ gap: 16, maxWidth: 980 }}>
      <section className="card card--pad">
        <div className="row" style={{ marginBottom: 10 }}>
          <div className="section-label">Image URLs in POST /predict</div>
          <div className="grow" />
          <Badge tone={record.mode === 'off' ? 'default' : 'ok'}>
            {record.mode === 'off'
              ? 'off'
              : record.mode === 'any'
                ? 'any public host'
                : 'allowlist'}
          </Badge>
          {!isAdmin && <span className="xs muted">Read-only: an administrator changes this.</span>}
        </div>

        <div className="editor">
          <fieldset className="serving__modes serving__modes--three" disabled={!isAdmin}>
            <legend className="field__label">DOWNLOAD IMAGES FROM</legend>
            {MODES.map((option) => (
              <label
                key={option.value}
                className={`serving__mode ${mode === option.value ? 'serving__mode--on' : ''}`}
              >
                <input
                  type="radio"
                  name="image-url-mode"
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                />
                <span>
                  <strong>{option.label}</strong>
                  <span className="xs muted">{option.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <TextArea
            label="ALLOWED HOSTS"
            mono
            rows={6}
            disabled={!isAdmin}
            value={hostsText}
            onChange={(event) => setHostsText(event.target.value)}
            placeholder={'images.example.com\n*.s3.eu-west-1.amazonaws.com'}
            error={fieldError || (needsHosts ? 'Add at least one host for this mode.' : null)}
            hint="One per line. *.example.com covers every subdomain (not example.com itself). A pasted URL is reduced to its host. Kept when you switch modes."
          />

          <div className="small muted">
            {modeHint} Private and internal addresses (localhost, 10.x, 192.168.x, the cloud
            metadata address) are never downloaded from, whatever is chosen here. Each download may
            take {limits.timeout_s ?? '—'} s and follow {limits.max_redirects ?? '—'} redirects; one
            request&apos;s images may total {limits.max_request_mb ?? '—'} MB.
          </div>

          <div className="row">
            <span className="xs subtle">
              {record.source === 'saved'
                ? `Last changed by ${record.updated_by || 'unknown'} ${relativeTime(record.updated_at)}`
                : 'Not changed yet — image URLs are off by default.'}
            </span>
            <div className="grow" />
            {isAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={save}
                loading={saving}
                disabled={!dirty || needsHosts}
              >
                Save
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
